/**
 * UI preferences — persona-driven feature gating + configurable bottom
 * nav. Lets a user tailor OPMODE to how they actually use it: a "Lean"
 * user sees workout + food + stats with no gamification; an "Operator"
 * gets the full tactical suite minus nutrition; "Full Suite" shows
 * everything; "Custom" flips individual areas.
 *
 * Three things this controls:
 *   1. enabledAreas — which feature areas are active. A disabled area
 *      vanishes from the bottom nav, the More hub, AND the dashboard.
 *   2. navTabs — which destinations occupy the bottom bar (Home + More
 *      are always present and not in this list).
 *   3. persona — the named preset, or 'custom' once the user deviates.
 *
 * Persistence: user doc field `uiPreferences` in Firestore, mirrored to
 * localStorage for instant first paint (nav can't wait on a network
 * round-trip). localStorage is the read-through cache; Firestore is the
 * source of truth across devices.
 *
 * Routes are NOT hard-gated — a disabled area's pages still work if
 * deep-linked. We only hide the navigation + surfacing affordances.
 */

// ============================================================================
// FEATURE AREAS + DESTINATIONS
// ============================================================================

export type FeatureArea =
  | 'training'      // session, programs, templates, custom, accessories, 1rm, conditioning, range, sprint, injury, travel
  | 'nutrition'     // fuel
  | 'recovery'      // recovery, wearables
  | 'stats'         // metrics / SITREP / analytics
  | 'gamification'  // barracks, leaderboard, archetypes, XP
  | 'operations'    // operations / territory war
  | 'challenges'    // pillar challenges
  | 'progress';     // history, assessments

export const FEATURE_AREAS: FeatureArea[] = [
  'training', 'nutrition', 'recovery', 'stats', 'gamification', 'operations', 'challenges', 'progress',
];

export const AREA_LABELS: Record<FeatureArea, string> = {
  training: 'Training',
  nutrition: 'Nutrition',
  recovery: 'Recovery',
  stats: 'Stats & Intel',
  gamification: 'Gamification',
  operations: 'Operations',
  challenges: 'Challenges',
  progress: 'Progress & History',
};

export const AREA_DESCRIPTIONS: Record<FeatureArea, string> = {
  training: 'Workouts, programs, 1RM, conditioning, range — the core training engine.',
  nutrition: 'Food logging, macros, calorie targets.',
  recovery: 'Readiness, recovery analytics, wearable data.',
  stats: 'SITREP, Operator Score, analytics dashboards.',
  gamification: 'Barracks, ranks, archetypes, XP, leaderboard.',
  operations: 'Operations & territory war (Spearhead).',
  challenges: 'Pillar challenges (75 Hard-style discipline tracks).',
  progress: 'Workout history and fitness assessments.',
};

/** Hub category groupings for the More page. */
export type HubCategory = 'TRAIN' | 'RECON' | 'FUEL' | 'COMMAND' | 'SYSTEM';

export interface Destination {
  /** Route path. Also the stable key used in navTabs. */
  key: string;
  label: string;
  /** lucide-react icon name (resolved in the UI layer). */
  icon: string;
  area: FeatureArea | 'core';   // 'core' = always available (Home, Settings)
  category: HubCategory;
  /** Hide from the More hub grid (e.g. Home, which is always a tab). */
  hideFromHub?: boolean;
}

/**
 * Every navigable destination. `area` ties it to a feature toggle;
 * 'core' destinations are always shown. Icons are lucide names mapped
 * in the nav/hub components so this module stays React-free.
 */
export const DESTINATIONS: Destination[] = [
  { key: '/',            label: 'Home',         icon: 'Home',        area: 'core',         category: 'SYSTEM', hideFromHub: true },
  { key: '/settings',    label: 'Settings',     icon: 'Settings',    area: 'core',         category: 'SYSTEM' },

  // TRAIN
  { key: '/session',     label: 'Workout',      icon: 'Dumbbell',    area: 'training',     category: 'TRAIN' },
  { key: '/programs/select', label: 'Programs', icon: 'ListChecks',  area: 'training',     category: 'TRAIN' },
  { key: '/templates',   label: 'Templates',    icon: 'LayoutGrid',  area: 'training',     category: 'TRAIN' },
  { key: '/workouts/custom', label: 'Custom',   icon: 'PlusSquare',  area: 'training',     category: 'TRAIN' },
  { key: '/1rm',         label: '1RM Calc',     icon: 'Calculator',  area: 'training',     category: 'TRAIN' },
  { key: '/travel',      label: 'Travel Mode',  icon: 'Plane',       area: 'training',     category: 'TRAIN' },
  { key: '/injury',      label: 'Injury Mode',  icon: 'HeartPulse',  area: 'training',     category: 'TRAIN' },

  // RECON
  { key: '/metrics',     label: 'Stats',        icon: 'BarChart3',   area: 'stats',        category: 'RECON' },
  { key: '/recovery',    label: 'Recovery',     icon: 'Heart',       area: 'recovery',     category: 'RECON' },
  { key: '/history',     label: 'History',      icon: 'History',     area: 'progress',     category: 'RECON' },
  { key: '/assessments', label: 'Assessments',  icon: 'ClipboardCheck', area: 'progress',  category: 'RECON' },

  // FUEL
  { key: '/fuel',        label: 'Fuel',         icon: 'Utensils',    area: 'nutrition',    category: 'FUEL' },

  // COMMAND
  { key: '/operations',  label: 'Operations',   icon: 'Map',         area: 'operations',   category: 'COMMAND' },
  { key: '/barracks',    label: 'Barracks',     icon: 'Shield',      area: 'gamification', category: 'COMMAND' },
  { key: '/challenge',   label: 'Challenge',    icon: 'Flame',       area: 'challenges',   category: 'COMMAND' },
];

export function getDestination(key: string): Destination | undefined {
  return DESTINATIONS.find(d => d.key === key);
}

// ============================================================================
// PERSONAS
// ============================================================================

export type Persona = 'full' | 'tactical' | 'operator' | 'hypertrophy' | 'lean' | 'biohacker' | 'custom';

export const PERSONA_LABELS: Record<Persona, string> = {
  full: 'Full Suite',
  tactical: 'Tactical',
  operator: 'Operator',
  hypertrophy: 'Hypertrophy',
  lean: 'Lean',
  biohacker: 'Biohacker',
  custom: 'Custom',
};

export const PERSONA_TAGLINES: Record<Persona, string> = {
  full: 'Everything OPMODE offers.',
  tactical: 'SOF readiness console — physical, mental & spiritual readiness. A force multiplier.',
  operator: 'Gamified — operations, territory, ranks & missions. The mission is the hook.',
  hypertrophy: 'Heavy-lifter focus — volume per muscle, protein, physique, PR tracking.',
  lean: 'Just workout, food, and stats. No gamification or ops.',
  biohacker: 'Quantified self — HRV, sleep, recovery & nutrition precision. Optimize every system.',
  custom: 'Your own mix.',
};

const allAreas = (v: boolean): Record<FeatureArea, boolean> =>
  FEATURE_AREAS.reduce((acc, a) => { acc[a] = v; return acc; }, {} as Record<FeatureArea, boolean>);

/** Areas enabled per persona. 'custom' is seeded from whatever the user
 *  had when they switched to it (handled at call site). */
export const PERSONA_AREAS: Record<Exclude<Persona, 'custom'>, Record<FeatureArea, boolean>> = {
  full: allAreas(true),
  // SOF readiness console — physical/mental/spiritual readiness, deep
  // performance data, zero game layer. Challenges ON because the
  // mental + spiritual pillars draw from the pillar-discipline system.
  tactical: {
    training: true, nutrition: true, recovery: true, stats: true,
    progress: true, challenges: true, gamification: false, operations: false,
  },
  // Gamified user — operations/ranks/missions/challenges are the hook.
  operator: {
    training: true, recovery: true, stats: true, gamification: true,
    operations: true, challenges: true, progress: true, nutrition: false,
  },
  // Heavy lifter / bodybuilder — volume, protein, physique. No game.
  hypertrophy: {
    training: true, nutrition: true, recovery: true, stats: true,
    progress: true, gamification: false, operations: false, challenges: false,
  },
  lean: {
    training: true, nutrition: true, recovery: true, stats: true,
    progress: true, gamification: false, operations: false, challenges: false,
  },
  // Quantified-self optimizer — recovery + nutrition precision + deep
  // stats are the whole point. No game layer, no ops.
  biohacker: {
    training: true, nutrition: true, recovery: true, stats: true,
    progress: true, gamification: false, operations: false, challenges: false,
  },
};

/**
 * Default bottom-bar tabs per persona (between Home and More).
 *
 * NOTE: /session is intentionally NOT in any persona's defaults — the
 * bottom-nav's center FAB owns "Start workout", so duplicating it as a
 * tab wastes a slot. Pick the next-most-relevant destination instead.
 */
export const PERSONA_NAV_TABS: Record<Exclude<Persona, 'custom'>, string[]> = {
  full:        ['/metrics',    '/fuel',       '/barracks'],
  tactical:    ['/metrics',    '/recovery',   '/operations'],
  operator:    ['/operations', '/barracks',   '/metrics'],
  hypertrophy: ['/fuel',       '/metrics',    '/recovery'],
  lean:        ['/fuel',       '/metrics',    '/recovery'],
  biohacker:   ['/recovery',   '/metrics',    '/fuel'],
};

// ============================================================================
// PREFERENCES SHAPE + DEFAULTS
// ============================================================================

export interface UiPreferences {
  persona: Persona;
  enabledAreas: Record<FeatureArea, boolean>;
  /** Ordered route keys for the bottom bar (excludes Home + More). Max 3. */
  navTabs: string[];
}

export const DEFAULT_UI_PREFERENCES: UiPreferences = {
  persona: 'full',
  enabledAreas: allAreas(true),
  navTabs: [...PERSONA_NAV_TABS.full],
};

/** Build a full UiPreferences object for a named persona. */
export function preferencesForPersona(persona: Exclude<Persona, 'custom'>): UiPreferences {
  return {
    persona,
    enabledAreas: { ...PERSONA_AREAS[persona] },
    navTabs: [...PERSONA_NAV_TABS[persona]],
  };
}

/** Is a feature area enabled under these prefs? 'core' is always true. */
export function isAreaEnabled(prefs: UiPreferences | null, area: FeatureArea | 'core'): boolean {
  if (area === 'core') return true;
  if (!prefs) return true; // pre-load: show everything rather than flash-hide
  return prefs.enabledAreas[area] !== false;
}

/** Is a specific destination reachable (its area enabled)? */
export function isDestinationEnabled(prefs: UiPreferences | null, key: string): boolean {
  const dest = getDestination(key);
  if (!dest) return true;
  return isAreaEnabled(prefs, dest.area);
}

// Sanitize/migrate a loaded prefs blob — fills missing areas, clamps
// navTabs to enabled+existing destinations, caps at 3.
export function normalizePreferences(raw: Partial<UiPreferences> | null | undefined): UiPreferences {
  if (!raw) return { ...DEFAULT_UI_PREFERENCES, enabledAreas: allAreas(true) };
  const enabledAreas = { ...allAreas(true), ...(raw.enabledAreas ?? {}) };
  const persona: Persona = raw.persona ?? 'full';
  let navTabs = (raw.navTabs ?? DEFAULT_UI_PREFERENCES.navTabs)
    .filter(k => getDestination(k) && k !== '/')      // valid, non-Home
    .filter((k, i, arr) => arr.indexOf(k) === i)       // de-dupe
    .slice(0, 3);
  // Drop tabs whose area got disabled; backfill from persona defaults.
  navTabs = navTabs.filter(k => {
    const d = getDestination(k);
    return d ? enabledAreas[d.area as FeatureArea] !== false : false;
  });
  if (navTabs.length === 0) {
    navTabs = (persona !== 'custom' ? PERSONA_NAV_TABS[persona] : DEFAULT_UI_PREFERENCES.navTabs)
      .filter(k => { const d = getDestination(k); return d ? enabledAreas[d.area as FeatureArea] !== false : false; })
      .slice(0, 3);
  }
  return { persona, enabledAreas, navTabs };
}

// ============================================================================
// PERSISTENCE
// ============================================================================

const LS_KEY = 'opmode:uiPreferences';

export function readCachedPreferences(): UiPreferences | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return normalizePreferences(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeCachedPreferences(prefs: UiPreferences): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(prefs)); } catch { /* full / private */ }
}

/** Load from Firestore (source of truth), falling back to cache then
 *  defaults. Also refreshes the localStorage cache. */
export async function loadUiPreferences(userId: string): Promise<UiPreferences> {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    const snap = await getDoc(doc(db, 'users', userId));
    const data = snap.exists() ? snap.data() : null;
    const prefs = normalizePreferences(data?.uiPreferences ?? null);
    writeCachedPreferences(prefs);
    return prefs;
  } catch (e) {
    console.warn('[ui-prefs] load failed, using cache/defaults:', e);
    return readCachedPreferences() ?? { ...DEFAULT_UI_PREFERENCES };
  }
}

/** Persist to Firestore + cache. Optimistic: cache is written first so
 *  the UI updates instantly even if the network write lags. */
export async function saveUiPreferences(userId: string, prefs: UiPreferences): Promise<void> {
  const normalized = normalizePreferences(prefs);
  writeCachedPreferences(normalized);
  try {
    const { doc, setDoc } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    await setDoc(doc(db, 'users', userId), { uiPreferences: normalized }, { merge: true });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('opmode-ui-prefs-changed'));
    }
  } catch (e) {
    console.warn('[ui-prefs] save failed (cache still updated):', e);
  }
}
